import React, { useState, useMemo, useCallback } from 'react';
import { motion } from 'motion/react';
import { 
  DollarSign, 
  ArrowRight, 
  HelpCircle, 
  Info, 
  CheckCircle, 
  ShieldAlert, 
  TrendingUp, 
  Database, 
  Coins, 
  Globe,
  RefreshCw,
  QrCode
} from 'lucide-react';
import * as d3 from 'd3';
import { generateQRBlocks } from './NexoraXPaymentGateway';

interface FlowNode {
  id: string;
  label: string;
  sublabel: string;
  x: number;
  y: number;
  color: string;
  bgColor: string;
  type: 'source' | 'middle' | 'destination';
  details: string[];
  state?: 'SECURED' | 'ACTIVE_ROUTING' | 'BLOCKED';
}

interface FlowLink {
  id: string;
  source: string;
  target: string;
  color: string;
  label: string;
  value: number;
  pct: string;
}

export function ZakatFlowDiagram() {
  const [grossHarvest, setGrossHarvest] = useState<number>(2820000); // NexoraX Ledger Total Gross Harvest Default
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>('ZAKAT_LEVY');
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'GROSS_HARVEST' | 'NET_SETTLEMENT'>('GROSS_HARVEST');
  const [strictFiltering, setStrictFiltering] = useState<boolean>(true); // Strict D3 path filtering active default

  // Dynamic QR Code Overlay State
  const [activeQrData, setActiveQrData] = useState<{
    isOpen: boolean;
    gateway: string;
    label: string;
    link: string;
    qrAsset: string;
    note: string;
    isLoading: boolean;
  } | null>(null);

  const triggerQrSync = async (nodeId: string) => {
    let gw = 'SecurePayment';
    if (nodeId === 'NET_SETTLEMENT' || nodeId === 'SecurePayment_BANK_SETTLEMENT') gw = 'SecurePayment';
    else if (nodeId === 'GROSS_HARVEST') gw = 'stripe';
    else if (nodeId === 'GAVEL_FEE' || nodeId === 'HMRC_REVENUE') gw = 'gocardless';
    else gw = 'paypal';

    setActiveQrData({
      isOpen: true,
      gateway: gw,
      label: gw.toUpperCase(),
      link: '',
      qrAsset: '',
      note: '',
      isLoading: true
    });

    try {
      const res = await fetch(`/api/empire/qr?gateway=${gw}`);
      const data = await res.json();
      if (data.status === 'success') {
        setActiveQrData({
          isOpen: true,
          gateway: gw,
          label: data.label,
          link: data.link,
          qrAsset: data.qrAsset,
          note: data.note,
          isLoading: false
        });
      } else {
        throw new Error(data.message || 'Verification Layer Error');
      }
    } catch (err) {
      console.error(err);
      setActiveQrData(prev => prev ? {
        ...prev,
        isLoading: false,
        note: 'Failed to sync with Empire OS Core Registry.'
      } : null);
    }
  };

  // D3 Zooming & Scaling reference states
  const svgRef = React.useRef<SVGSVGElement | null>(null);
  const zoomBehaviorRef = React.useRef<any>(null);
  const [zoomScale, setZoomScale] = useState<number>(1);
  const [zoomX, setZoomX] = useState<number>(0);
  const [zoomY, setZoomY] = useState<number>(0);

  // Initialize interactive D3 zoom behaviors
  React.useEffect(() => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.5, 4.0])
      .on('zoom', (event) => {
        setZoomScale(event.transform.k);
        setZoomX(event.transform.x);
        setZoomY(event.transform.y);
      });

    zoomBehaviorRef.current = zoom;
    svg.call(zoom);
  }, []);

  const handleZoomIn = () => {
    if (svgRef.current && zoomBehaviorRef.current) {
      d3.select(svgRef.current)
        .transition()
        .duration(320)
        .call(zoomBehaviorRef.current.scaleBy, 1.25);
    }
  };

  const handleZoomOut = () => {
    if (svgRef.current && zoomBehaviorRef.current) {
      d3.select(svgRef.current)
        .transition()
        .duration(320)
        .call(zoomBehaviorRef.current.scaleBy, 0.8);
    }
  };

  const handleZoomReset = () => {
    if (svgRef.current && zoomBehaviorRef.current) {
      d3.select(svgRef.current)
        .transition()
        .duration(400)
        .call(zoomBehaviorRef.current.transform, d3.zoomIdentity);
    }
  };

  // Helper to determine if a connection path is currently active under the selected view
  const getLinkActiveState = useCallback((id: string) => {
    if (activeView === 'GROSS_HARVEST') {
      // Gross Harvest view highlights input paths, Zakat levy splits, and 10% Maintenance & Hosting Fee basis routing
      return [
        'gross-to-zakat',
        'gross-to-gavel',
        'gross-to-net',
        'zakat-to-dunfermline',
        'zakat-to-global',
        'zakat-to-uk',
        'gavel-to-gov'
      ].includes(id);
    } else {
      // Net finance Settled view isolates and prioritizes strictly the Net Settlement sequence
      return [
        'gross-to-net',
        'net-to-SecurePayment'
      ].includes(id);
    }
  }, [activeView]);

  // Helper to determine if a node is active in the current view context
  const getNodeActiveState = useCallback((id: string) => {
    if (activeView === 'GROSS_HARVEST') {
      return [
        'GROSS_HARVEST',
        'ZAKAT_LEVY',
        'GAVEL_FEE',
        'NET_SETTLEMENT',
        'CHARITY_DUNFERMLINE',
        'CHARITY_GLOBAL',
        'CHARITY_UK',
        'HMRC_REVENUE'
      ].includes(id);
    } else {
      return [
        'GROSS_HARVEST',
        'NET_SETTLEMENT',
        'SecurePayment_BANK_SETTLEMENT'
      ].includes(id);
    }
  }, [activeView]);

  // Financial calculations corresponding strictly with the Tiered Extraction Protocol
  const calculations = useMemo(() => {
    // 1. Calculate the 1% 3% Community Allocation for the Global Foundation
    const NexoraXLevy = grossHarvest * 0.01;

    // Split Zakat 1% Levy exactly equally (33.33% each)
    const charity1 = NexoraXLevy / 3; // Dunfermline Node
    const charity2 = NexoraXLevy / 3; // Global Node
    const charity3 = NexoraXLevy / 3; // UK Node

    // Deduct 1% Levy first, then calculate 10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee)
    const remainingAfterLevy = grossHarvest - NexoraXLevy;
    const lifetimeTax = remainingAfterLevy * 0.20;

    // Remaining Net finance Settlement routed to Secure Payment Provider
    const netSettlement = remainingAfterLevy - lifetimeTax;

    return {
      grossHarvest,
      NexoraXLevy,
      charity1,
      charity2,
      charity3,
      remainingAfterLevy,
      lifetimeTax,
      netSettlement
    };
  }, [grossHarvest]);

  // Nodes for D3 coordinate template
  // SVG coordinates: Width 960, Height 460
  const nodes: FlowNode[] = useMemo(() => [
    {
      id: 'GROSS_HARVEST',
      label: 'SYSTEM GROSS HARVEST',
      sublabel: 'Raw Inbound Node',
      x: 80,
      y: 230,
      color: '#38bdf8', // Blue
      bgColor: 'rgba(56, 189, 248, 0.1)',
      type: 'source',
      details: [
        `Operational Volume: £${calculations.grossHarvest.toLocaleString()}`,
        'Origin: Multi-channel commercial processing grid',
        'Direct tracking under Westminster guidelines',
        'Verification Layer: CloudSync-02 Active Sync'
      ],
      state: 'ACTIVE_ROUTING'
    },
    {
      id: 'ZAKAT_LEVY',
      label: '1% 3% Community Allocation',
      sublabel: 'Zakat Perpetual Reserve',
      x: 380,
      y: 110,
      color: '#fbbf24', // Amber/Gold
      bgColor: 'rgba(251, 191, 36, 0.1)',
      type: 'middle',
      details: [
        `Zakat Allocation: £${calculations.NexoraXLevy.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        'Levy Metric: Strict 1% Gross Anchor',
        'Religious Compliance: Instant charitable partition',
        'Redistribution Interval: Daily automated settlement'
      ],
      state: 'ACTIVE_ROUTING'
    },
    {
      id: 'GAVEL_FEE',
      label: '10% Maintenance & Hosting Fee',
      sublabel: 'NexoraX Gavel Levy',
      x: 380,
      y: 230,
      color: '#a78bfa', // Purple
      bgColor: 'rgba(167, 139, 250, 0.1)',
      type: 'middle',
      details: [
        `Tax Accrual: £${calculations.lifetimeTax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        'Formula: 20% on (Gross Harvest minus 1% 3% Community Allocation)',
        'Authority: UK Statutory Corporate Alignment',
        'Recipient: UK Central Revenue Governance'
      ],
      state: 'ACTIVE_ROUTING'
    },
    {
      id: 'NET_SETTLEMENT',
      label: 'NET finance SETTLEMENT',
      sublabel: 'Secure Payment Provider Liquid finance',
      x: 380,
      y: 350,
      color: '#10b981', // Emerald
      bgColor: 'rgba(16, 185, 129, 0.1)',
      type: 'middle',
      details: [
        `Transfer Volume: £${calculations.netSettlement.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        'Destination Channel: Secure Payment Provider Escrow Protocol',
        'Sort Code: Verified Payment Configuration | Account: Secure Billing Provider',
        'Operational Status: Cleared and Audited'
      ],
      state: 'SECURED'
    },
    {
      id: 'CHARITY_DUNFERMLINE',
      label: 'DUNFERMLINE CHARITY',
      sublabel: 'Charity Node 1 (Local)',
      x: 750,
      y: 50,
      color: '#fb7185', // Rose
      bgColor: 'rgba(251, 113, 133, 0.1)',
      type: 'destination',
      details: [
        `Disbursement Amount: £${calculations.charity1.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        'Regional Target: Fife (Dunfermline)',
        'Core Objective: Humanitarian support & localized integration',
        'Status: Vetted and Active'
      ],
      state: 'SECURED'
    },
    {
      id: 'CHARITY_GLOBAL',
      label: 'GLOBAL FOUNDATION',
      sublabel: 'Charity Node 2 (Worldwide)',
      x: 750,
      y: 110,
      color: '#fbbf24', // Amber/Gold
      bgColor: 'rgba(251, 191, 36, 0.1)',
      type: 'destination',
      details: [
        `Disbursement Amount: £${calculations.charity2.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        'International Objective: Global Humanitarian Relief Network',
        'Monitoring Protocol: Westminster NGO Standards',
        'Status: Vetted and Active'
      ],
      state: 'SECURED'
    },
    {
      id: 'CHARITY_UK',
      label: 'UK COMMUNITY INITIATIVE',
      sublabel: 'Charity Node 3 (National)',
      x: 750,
      y: 170,
      color: '#f472b6', // Pink
      bgColor: 'rgba(244, 114, 182, 0.1)',
      type: 'destination',
      details: [
        `Disbursement Amount: £${calculations.charity3.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        'National Target: UK Education & Social Mobility Care',
        'Sponsorship Alliance: NexoraX billing records scheme (Secure Credential Stored Server-Side)',
        'Status: Vetted and Active'
      ],
      state: 'SECURED'
    },
    {
      id: 'HMRC_REVENUE',
      label: 'UK GOV TAX ACCOUNT',
      sublabel: 'Statutory Corporate Levy',
      x: 750,
      y: 260,
      color: '#e2e8f0', // Slate/White
      bgColor: 'rgba(226, 232, 240, 0.1)',
      type: 'destination',
      details: [
        `Transferred: £${calculations.lifetimeTax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        'Regulatory Alignment: HMRC Corporate Profit tax protocol',
        'Audit Baseline: Full compliance check',
        'Uptime Verification: 100% Guaranteed'
      ],
      state: 'SECURED'
    },
    {
      id: 'SecurePayment_BANK_SETTLEMENT',
      label: 'SecurePayment MASTER LEDGER',
      sublabel: 'Verified Payment Configuration | A/C Secure Billing Provider',
      x: 750,
      y: 370,
      color: '#10b981', // Emerald
      bgColor: 'rgba(16, 185, 129, 0.1)',
      type: 'destination',
      details: [
        `Net Deposited: £${calculations.netSettlement.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        'Bank Holder: NexoraX Digital Ltd (SC887095)',
        'Branch Network: SecurePayment Business Bank London',
        'Dual-Signature Protocol: Office of the Director'
      ],
      state: 'SECURED'
    }
  ], [calculations]);

  // Links connecting the nodes with flowing values with dynamic view proportions
  const links: FlowLink[] = useMemo(() => {
    const isNetFocus = activeView === 'NET_SETTLEMENT';
    return [
      {
        id: 'gross-to-zakat',
        source: 'GROSS_HARVEST',
        target: 'ZAKAT_LEVY',
        color: '#fbbf24',
        label: isNetFocus ? 'Zakat Ratio relative to Net Core' : 'Perpetual Community Allocation',
        value: calculations.NexoraXLevy,
        pct: isNetFocus 
          ? `${((calculations.NexoraXLevy / calculations.netSettlement) * 100).toFixed(2)}%` 
          : '1.0%'
      },
      {
        id: 'gross-to-gavel',
        source: 'GROSS_HARVEST',
        target: 'GAVEL_FEE',
        color: '#a78bfa',
        label: isNetFocus ? '10% Maintenance & Hosting Fee Ratio relative to Net Core' : 'Gavel Allocation Base (Tax)',
        value: calculations.lifetimeTax,
        pct: isNetFocus 
          ? `${((calculations.lifetimeTax / calculations.netSettlement) * 100).toFixed(2)}%` 
          : '20.0%'
      },
      {
        id: 'gross-to-net',
        source: 'GROSS_HARVEST',
        target: 'NET_SETTLEMENT',
        color: '#10b981',
        label: isNetFocus ? 'NexoraX Core Inflow Route' : 'Core Net Balance Flow',
        value: calculations.netSettlement,
        pct: isNetFocus ? '100.0% FOCUS' : '79.0%'
      },
      // Zakat splits
      {
        id: 'zakat-to-dunfermline',
        source: 'ZAKAT_LEVY',
        target: 'CHARITY_DUNFERMLINE',
        color: '#fb7185',
        label: 'Charity 1 Distribution (Dunfermline)',
        value: calculations.charity1,
        pct: isNetFocus 
          ? `${((calculations.charity1 / calculations.netSettlement) * 100).toFixed(2)}%` 
          : '33.33%'
      },
      {
        id: 'zakat-to-global',
        source: 'ZAKAT_LEVY',
        target: 'CHARITY_GLOBAL',
        color: '#fbbf24',
        label: 'Charity 2 Distribution (Global)',
        value: calculations.charity2,
        pct: isNetFocus 
          ? `${((calculations.charity2 / calculations.netSettlement) * 100).toFixed(2)}%` 
          : '33.33%'
      },
      {
        id: 'zakat-to-uk',
        source: 'ZAKAT_LEVY',
        target: 'CHARITY_UK',
        color: '#f472b6',
        label: 'Charity 3 Distribution (UK)',
        value: calculations.charity3,
        pct: isNetFocus 
          ? `${((calculations.charity3 / calculations.netSettlement) * 100).toFixed(2)}%` 
          : '33.33%'
      },
      // Gavel splits
      {
        id: 'gavel-to-gov',
        source: 'GAVEL_FEE',
        target: 'HMRC_REVENUE',
        color: '#c084fc',
        label: 'Statutory Contribution Route',
        value: calculations.lifetimeTax,
        pct: isNetFocus 
          ? `${((calculations.lifetimeTax / calculations.netSettlement) * 100).toFixed(2)}%` 
          : '100%'
      },
      // Net splits
      {
        id: 'net-to-SecurePayment',
        source: 'NET_SETTLEMENT',
        target: 'SecurePayment_BANK_SETTLEMENT',
        color: '#34d399',
        label: 'Secure Payment Provider Inward Settlement',
        value: calculations.netSettlement,
        pct: '100%'
      }
    ];
  }, [calculations, activeView]);

  // Dynamic stroke widths depending on active view context to emphasize flows
  const getLinkStrokeWidth = (link: FlowLink, isHovered: boolean) => {
    let baseWidth = 3;
    
    if (activeView === 'GROSS_HARVEST') {
      // Direct proportion outward from Gross
      if (link.id === 'gross-to-net') baseWidth = 8;
      else if (link.id === 'gross-to-gavel') baseWidth = 4.5;
      else if (link.id === 'gross-to-zakat') baseWidth = 2;
      else if (link.id === 'net-to-SecurePayment') baseWidth = 6;
      else if (link.id === 'gavel-to-gov') baseWidth = 3.5;
      else baseWidth = 1.6;
    } else {
      // Focus heavily on Net finance channels
      if (link.id === 'gross-to-net') baseWidth = 10;
      else if (link.id === 'net-to-SecurePayment') baseWidth = 12; // High-prominence path
      else if (link.id === 'gross-to-gavel') baseWidth = 1.2; // Diminished in net view
      else if (link.id === 'gross-to-zakat') baseWidth = 1.2; // Diminished in net view
      else if (link.id === 'gavel-to-gov') baseWidth = 1.5;
      else baseWidth = 1.0;
    }

    return isHovered ? baseWidth * 2.2 : baseWidth;
  };

  // Compile bezier generator horizontal link
  const generateBezier = (link: FlowLink) => {
    const srcNode = nodes.find(n => n.id === link.source);
    const tgtNode = nodes.find(n => n.id === link.target);
    if (!srcNode || !tgtNode) return '';
    
    const x0 = srcNode.x;
    const y0 = srcNode.y;
    const x1 = tgtNode.x;
    const y1 = tgtNode.y;
    const midX = (x0 + x1) / 2;
    return `M ${x0} ${y0} C ${midX} ${y0}, ${midX} ${y1}, ${x1} ${y1}`;
  };

  const selectedNode = nodes.find(n => n.id === selectedNodeId);

  return (
    <div className="w-full bg-[#070b16] border border-slate-800/80 rounded-2xl p-6 relative overflow-hidden flex flex-col gap-6">
      <style>{`
        @keyframes d3Flow {
          to {
            stroke-dashoffset: -30;
          }
        }
        @keyframes d3FlowFast {
          to {
            stroke-dashoffset: -40;
          }
        }
        @keyframes spinSlow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .flowing-zakat-path {
          stroke-dasharray: 8, 4;
          animation: d3Flow 2s linear infinite;
        }
        .flowing-zakat-path-fast {
          stroke-dasharray: 6, 3;
          animation: d3Flow 1.2s linear infinite;
        }
        .flowing-SecurePayment-path-hyper {
          stroke-dasharray: 12, 4;
          animation: d3FlowFast 0.75s linear infinite;
        }
        .animate-spin-slow {
          animation: spinSlow 20s linear infinite;
        }
        .custom-cyber-marker {
          transform-box: fill-box;
          transform-origin: center;
          transition: transform 0.15s ease-out, opacity 0.35s ease-in-out;
        }
      `}</style>
      
      {/* Background ambient lighting effects to establish premium architecture */}
      <div className="absolute -top-24 -left-20 w-80 h-80 bg-blue-500/5 blur-[100px] rounded-full pointer-events-none"></div>
      <div className="absolute -bottom-24 -right-20 w-80 h-80 bg-gold-500/5 blur-[100px] bg-amber-500/5 rounded-full pointer-events-none"></div>

      {/* Header section with status telemetry */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-slate-800/80 pb-5 gap-4">
        <div>
          <div className="flex items-center gap-2 text-amber-500 font-bold text-[10px] uppercase tracking-[0.3em] font-mono">
            <Coins className="w-3.5 h-3.5 animate-pulse" />
            NEXORAX ROYAL COFFEE & COCOA TRADING CO / HIGH-POTENCY ZAKAT PORTAL
          </div>
          <h2 className="text-2xl font-sans font-black text-white uppercase tracking-tighter mt-1">
            ZAKAT LEVY INTERACTIVE FLOW MATRIX
          </h2>
          <p className="text-[10px] text-slate-400 font-mono uppercase tracking-wider mt-1">
            Visualizing the 1% 3% Community Allocation and 10% Maintenance & Hosting Fee under Westminster accounting guidelines
          </p>
        </div>

        <div className="bg-[#0b1226] border border-amber-500/20 px-3 py-1.5 rounded-lg flex items-center gap-2 text-[10px] font-mono text-amber-400 uppercase font-bold tracking-wider">
          <div className="w-2.5 h-2.5 rounded-full bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.8)] animate-pulse"></div>
          D3 RENDERING: VALID
        </div>
      </div>

      {/* Volume Selector / Slider controls */}
      <div className="bg-[#02050c]/90 border border-slate-800/80 p-5 rounded-xl flex flex-col xl:flex-row xl:items-center justify-between gap-6">
        <div className="flex-1 space-y-2 text-left">
          <label className="text-xs font-mono text-slate-400 font-bold uppercase tracking-widest flex justify-between items-center">
            <span>DIRECTOR COGNITIVE GROSS FLOW CONTROLLER</span>
            <span className="text-cyan-400 text-sm font-black font-mono">
              £{grossHarvest.toLocaleString()}
            </span>
          </label>
          <div className="flex items-center gap-4">
            <span className="text-[10px] font-mono text-slate-500 font-bold">£100k</span>
            <input 
              type="range" 
              min={100000} 
              max={10000000} 
              step={50000}
              value={grossHarvest}
              id="directorGrossFlowController"
              onChange={(e) => setGrossHarvest(Number(e.target.value))}
              className="flex-grow accent-cyan-500 h-1 bg-[#12192c] rounded-lg appearance-none cursor-ew-resize transition-all hover:bg-[#18233e]"
            />
            <span className="text-[10px] font-mono text-slate-500 font-bold">£10.0M</span>
          </div>
        </div>

        {/* Dynamic Interactive Perspective Toggle */}
        <div className="flex flex-col gap-1.5 shrink-0 xl:border-l xl:border-slate-800/80 xl:pl-6 text-left">
          <span className="text-[9px] font-mono text-slate-500 font-bold uppercase tracking-widest block font-extrabold">
            INTERACTIVE PERSPECTIVE NODE CO-EFFICIENT
          </span>
          <div className="flex bg-[#040813] border border-slate-800 p-1 rounded-xl">
            <button
              id="gross-harvest-view-toggle"
              onClick={() => {
                setActiveView('GROSS_HARVEST');
                setSelectedNodeId('GROSS_HARVEST');
              }}
              className={`px-3.5 py-1.5 rounded-lg font-mono text-[9px] uppercase font-bold tracking-wider transition-all duration-200 ${
                activeView === 'GROSS_HARVEST' 
                  ? 'bg-cyan-500 text-black shadow-[0_0_12px_rgba(6,182,212,0.3)] font-black'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              GROSS HARVEST VIEW
            </button>
            <button
              id="net-finance-view-toggle"
              onClick={() => {
                setActiveView('NET_SETTLEMENT');
                setSelectedNodeId('SecurePayment_BANK_SETTLEMENT'); // Focus directly on the Secure Payment Provider routing node in Net view
              }}
              className={`px-3.5 py-1.5 rounded-lg font-mono text-[9px] uppercase font-bold tracking-wider transition-all duration-200 ${
                activeView === 'NET_SETTLEMENT'
                  ? 'bg-emerald-500 text-black shadow-[0_0_12px_rgba(16,185,129,0.3)] font-black'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              NET finance VIEW
            </button>
          </div>

          {/* Interactive filter toggle rule */}
          <div 
            id="strict-path-filter-toggle"
            className="flex items-center gap-1.5 mt-1 cursor-pointer select-none group" 
            onClick={() => setStrictFiltering(!strictFiltering)}
          >
            <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-all ${
              strictFiltering 
                ? 'bg-cyan-500 border-cyan-500 text-black' 
                : 'border-slate-700 bg-slate-950/80 group-hover:border-slate-500'
            }`}>
              {strictFiltering && (
                <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <span className="text-[9px] font-mono text-slate-400 font-bold uppercase tracking-wider group-hover:text-slate-200 transition-colors">
              Isolate Active Grid Pathways
            </span>
          </div>
        </div>

        <div className="flex gap-2 shrink-0 xl:border-l xl:border-slate-800/80 xl:pl-6 items-center">
          <button 
            onClick={() => setGrossHarvest(2820000)}
            className="px-3 py-2 hover:bg-[#101b35] border border-slate-800 text-slate-300 rounded-lg hover:text-white font-mono text-[9px] uppercase tracking-wider font-bold transition-all"
          >
            DEFAULT (2.82M)
          </button>
          <button 
            onClick={() => setGrossHarvest(5100000)}
            className="px-3 py-2 bg-amber-950/20 border border-amber-950/50 hover:bg-amber-950/40 text-amber-400 rounded-lg font-mono text-[9px] uppercase tracking-wider font-bold transition-all"
          >
            £150,000 Resource
          </button>
        </div>
      </div>

      {/* Main interactive visualization segment */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 w-full">
        {/* Left Side: Interactive SVG flow diagram */}
        <div className="lg:col-span-2 bg-[#02050c]/50 border border-slate-800/80 rounded-xl p-4 flex flex-col items-center justify-center relative min-h-[460px] overflow-hidden select-none">
          {/* Legend and interactive guides */}
          <div className="absolute top-4 left-4 flex gap-4 text-[9px] font-mono uppercase tracking-widest font-bold z-10 bg-[#02050c]/85 px-2.5 py-1.5 rounded-lg border border-slate-800/50">
            <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded bg-cyan-400"></div><span className="text-cyan-400">INPUT</span></div>
            <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded bg-amber-400"></div><span className="text-amber-400">LEVY</span></div>
            <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded bg-indigo-400"></div><span className="text-violet-400">TAX</span></div>
            <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded bg-emerald-400"></div><span className="text-emerald-400">finance</span></div>
          </div>

          <div className="absolute top-4 right-4 text-[9px] font-mono text-slate-500 uppercase tracking-widest bg-[#02050c]/85 px-2.5 py-1.5 rounded-lg border border-slate-800/50">
            PAN / PINCH / CLICK TO RECONCILE
          </div>

          {/* Zoom Controls Overlay */}
          <div className="absolute bottom-4 right-4 flex items-center gap-2 bg-[#070b16]/95 border border-slate-800 p-2 rounded-xl z-20 shadow-2xl pointer-events-auto">
            <span className="text-[9px] font-mono font-extrabold text-cyan-400 px-1.5 uppercase tracking-wider">
              SCALE: {Math.round(zoomScale * 100)}%
            </span>
            <div className="h-4 w-px bg-slate-800"></div>
            <button
              onClick={handleZoomOut}
              className="p-1 text-slate-400 hover:text-white hover:bg-slate-800/50 rounded-lg transition-all"
              title="Zoom Out"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
            <button
              onClick={handleZoomReset}
              className="px-2 py-0.5 text-[8px] font-mono font-extrabold text-slate-400 hover:text-white hover:bg-slate-850 rounded-md bg-[#02050c] border border-slate-800 transition-all"
              title="Reset Zoom to 100%"
            >
              RESET
            </button>
            <button
              onClick={handleZoomIn}
              className="p-1 text-slate-400 hover:text-white hover:bg-slate-800/50 rounded-lg transition-all"
              title="Zoom In"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          </div>

          {/* Core Cyber Status Legend Card */}
          <div className="absolute bottom-4 left-4 z-10 bg-[#02050c]/90 border border-slate-800 p-3 rounded-xl max-w-[210px] space-y-2 shadow-[0_0_20px_rgba(0,0,0,0.85)] backdrop-blur-md text-left pointer-events-auto select-none">
            <span className="text-[8px] font-mono font-extrabold text-amber-500 tracking-[0.2em] block uppercase opacity-90 border-b border-slate-800/80 pb-1">
              CYBER BEACON PROTOCOLS
            </span>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-[9px] font-mono">
                <span className="flex items-center justify-center w-4 h-4 rounded-full bg-emerald-600/30 border border-emerald-500/50 shadow-[0_0_8px_rgba(16,185,129,0.4)]">
                  <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                  </svg>
                </span>
                <div>
                  <p className="text-emerald-400 font-extrabold text-[8px] uppercase tracking-tight leading-none">SECURED</p>
                  <p className="text-slate-400 text-[8px] leading-none mt-0.5">NexoraX finance</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-[9px] font-mono">
                <span className="flex items-center justify-center w-4 h-4 rounded-full bg-blue-600/30 border border-blue-500/50 shadow-[0_0_8px_rgba(59,130,246,0.4)] animate-pulse">
                  <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                    <polyline points="17 6 23 6 23 12" />
                  </svg>
                </span>
                <div>
                  <p className="text-blue-400 font-extrabold text-[8px] uppercase tracking-tight leading-none">ACTIVE ROUTING</p>
                  <p className="text-slate-400 text-[8px] leading-none mt-0.5">Billing Velocity Matrix</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-[9px] font-mono">
                <span className="flex items-center justify-center w-4 h-4 rounded-full bg-rose-600/30 border border-rose-500/50 shadow-[0_0_8px_rgba(244,63,94,0.4)]">
                  <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="#f43f5e" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                  </svg>
                </span>
                <div>
                  <p className="text-rose-400 font-extrabold text-[8px] uppercase tracking-tight leading-none">BLOCKED</p>
                  <p className="text-slate-400 text-[8px] leading-none mt-0.5">Threat/Intrusion Cutoff</p>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full max-w-[960px] aspect-[16/8]">
            <svg 
              ref={svgRef}
              viewBox="0 0 960 460" 
              width="100%" 
              height="100%"
              className="w-full h-full cursor-grab active:cursor-grabbing outline-none"
            >
              {/* Filter declarations for dropshadow glows */}
              <defs>
                <filter id="cyan-glow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="6" result="blur" />
                  <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>
                <filter id="gold-glow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="6" result="blur" />
                  <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>
                <filter id="emerald-glow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="6" result="blur" />
                  <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>
                <filter id="purple-glow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="6" result="blur" />
                  <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>
              </defs>

              <g transform={`translate(${zoomX}, ${zoomY}) scale(${zoomScale})`}>
                {/* Render Flow Links / Connections */}
                <g id="flow-links-group">
                  {links
                  .filter((link) => !strictFiltering || getLinkActiveState(link.id))
                  .map((link) => {
                    const isHovered = hoveredLink === link.id;
                    const isActive = getLinkActiveState(link.id);
                    
                    // Determine path opacity: dim when not active or when another link is focused
                    let opacity = 0.7;
                    if (hoveredLink) {
                      opacity = isHovered ? 1.0 : 0.15;
                    } else if (!isActive) {
                      opacity = 0.15;
                    }

                    const isZakatRelated = link.id.includes('zakat') || link.id.includes('gross-to-zakat');
                    const isNetFocusSecurePayment = link.id === 'net-to-SecurePayment' && activeView === 'NET_SETTLEMENT';
                    
                    const animateClass = isNetFocusSecurePayment 
                      ? 'flowing-SecurePayment-path-hyper' 
                      : (isZakatRelated ? 'flowing-zakat-path-fast' : 'flowing-zakat-path');
                    
                    return (
                      <g 
                        key={link.id}
                        onMouseEnter={() => setHoveredLink(link.id)}
                        onMouseLeave={() => setHoveredLink(null)}
                        className="cursor-pointer"
                      >
                        {/* Underlay glow path to establish thick glowing pipelines in dark theme */}
                        <path 
                          d={generateBezier(link)}
                          fill="none"
                          stroke={link.color}
                          strokeWidth={isHovered ? getLinkStrokeWidth(link, true) * 2.8 : getLinkStrokeWidth(link, false) * 2.5}
                          strokeOpacity={isHovered ? 0.3 : (isNetFocusSecurePayment ? 0.25 : 0.15)}
                          className="transition-all duration-300"
                        />
                        
                        {/* Main connection line */}
                        <path 
                          d={generateBezier(link)}
                          fill="none"
                          stroke={link.color}
                          strokeWidth={getLinkStrokeWidth(link, isHovered)}
                          strokeOpacity={opacity}
                          className="transition-all duration-300"
                        />

                        {/* Animated dash flow overlay with prioritized thickness/velocity for Secure Payment Provider */}
                        <path 
                          d={generateBezier(link)}
                          fill="none"
                          stroke={link.color}
                          strokeWidth={isNetFocusSecurePayment ? 4.5 : 2}
                          strokeOpacity={isHovered ? 0.9 : (isNetFocusSecurePayment ? 0.85 : 0.6)}
                          className={`transition-all duration-300 ${animateClass}`}
                        />

                      {/* Flow direction indicators */}
                      {isHovered && (
                        <foreignObject
                          x={(nodes.find(n => n.id === link.source)!.x + nodes.find(n => n.id === link.target)!.x) / 2 - 80}
                          y={(nodes.find(n => n.id === link.source)!.y + nodes.find(n => n.id === link.target)!.y) / 2 - 22}
                          width="160"
                          height="44"
                          className="pointer-events-none"
                        >
                          <div className="bg-[#0b1226]/95 border border-slate-700/80 rounded px-2.5 py-1 text-[9px] font-mono text-center shadow-lg uppercase leading-tight text-white font-bold">
                            <div>{link.label}</div>
                            <div className="text-cyan-400 mt-0.5">£{link.value.toLocaleString(undefined, { maximumFractionDigits: 0 })} ({link.pct})</div>
                          </div>
                        </foreignObject>
                      )}
                    </g>
                  );
                })}
              </g>

              {/* Render Flow Nodes */}
              <g id="flow-nodes-group">
                {nodes.map((node) => {
                  const isSelected = selectedNodeId === node.id;
                  const isHovered = hoveredLink ? (links.find(l => l.id === hoveredLink)?.source === node.id || links.find(l => l.id === hoveredLink)?.target === node.id) : false;
                  
                  const isNodeInActiveView = getNodeActiveState(node.id);
                  const nodeOpacity = strictFiltering && !isNodeInActiveView ? 0.12 : 1.0;
                  const isPointerDisabled = strictFiltering && !isNodeInActiveView;
                  
                  // Calculate dynamic scaling for the .custom-cyber-marker elements relative to the zoom level
                  const markerScale = 1.0 / Math.sqrt(zoomScale);

                  // Compute glow filters
                  let glowFilter = undefined;
                  if (isSelected || isHovered) {
                    if (node.color === '#fbbf24') glowFilter = 'url(#gold-glow)';
                    else if (node.color === '#10b981') glowFilter = 'url(#emerald-glow)';
                    else if (node.color === '#a78bfa') glowFilter = 'url(#purple-glow)';
                    else glowFilter = 'url(#cyan-glow)';
                  }

                  return (
                    <g 
                      key={node.id}
                      transform={`translate(${node.x}, ${node.y}) scale(${markerScale})`}
                      onClick={() => {
                        if (!isPointerDisabled) {
                          setSelectedNodeId(node.id);
                        }
                      }}
                      className="custom-cyber-marker cursor-pointer transition-all duration-350"
                      style={{ 
                        opacity: nodeOpacity,
                        pointerEvents: isPointerDisabled ? 'none' : 'auto'
                      }}
                    >
                      {/* Highlight rings specifically for Secure Payment Provider routing node under Net finance perspective */}
                      {node.id === 'SecurePayment_BANK_SETTLEMENT' && activeView === 'NET_SETTLEMENT' && (
                        <>
                          <circle
                            r={32}
                            fill="none"
                            stroke="#10b981"
                            strokeWidth="1.2"
                            strokeDasharray="4 2"
                            className="animate-spin-slow opacity-75"
                            style={{ transformOrigin: 'center' }}
                          />
                          <circle
                            r={26}
                            fill="none"
                            stroke="#34d399"
                            strokeWidth="0.8"
                            className="animate-pulse"
                            style={{ transformOrigin: 'center' }}
                          />
                          <circle
                            r={isSelected ? 22 : 18}
                            fill="rgba(16, 185, 129, 0.15)"
                            stroke="#34d399"
                            strokeWidth="1"
                            className="animate-ping"
                            style={{ transformOrigin: 'center', animationDuration: '3.5s' }}
                          />
                        </>
                      )}

                      {/* Interactive node container anchor */}
                      <circle 
                        r={isSelected ? 20 : 16} 
                        fill={node.bgColor}
                        stroke={node.color}
                        strokeWidth={isSelected ? 3 : 1.5}
                        filter={glowFilter}
                        className="transition-all duration-300"
                        style={{ filter: glowFilter }}
                      />

                      {/* Small center pulse point */}
                      <circle 
                        r={isSelected ? 5 : 3.5} 
                        fill={node.color}
                        className={`${isSelected ? 'animate-ping' : ''}`}
                        style={{ transformOrigin: 'center' }}
                      />

                      {/* Top-Right Status Badge representing State (SECURED/ACTIVE_ROUTING) */}
                      {node.state && (
                        <g transform="translate(14, -14)">
                          <circle
                            r="5"
                            fill={node.state === 'ACTIVE_ROUTING' ? '#2563eb' : '#059669'}
                            stroke="#ffffff"
                            strokeWidth="1.2"
                            className={node.state === 'ACTIVE_ROUTING' ? 'animate-pulse' : ''}
                            style={{ transformOrigin: 'center' }}
                          />
                        </g>
                      )}

                      {/* Interactive Button in the custom cyber marker to trigger dynamic QR sync */}
                      <foreignObject
                        x="-11"
                        y="18"
                        width="22"
                        height="22"
                        style={{ pointerEvents: 'auto' }}
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            triggerQrSync(node.id);
                          }}
                          className="w-full h-full bg-slate-950/90 border border-slate-705 border-slate-800 hover:border-amber-400 text-slate-400 hover:text-amber-400 rounded-md flex items-center justify-center transition-all cursor-pointer shadow-md"
                          title="Generate Node QR Gateway"
                        >
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="7" height="7" />
                            <rect x="14" y="3" width="7" height="7" />
                            <rect x="14" y="14" width="7" height="7" />
                            <rect x="3" y="14" width="7" height="7" />
                          </svg>
                        </button>
                      </foreignObject>

                      {/* Label Positioning: adjust left/right offset based on type */}
                      {node.type === 'source' ? (
                        <g transform="translate(-25, 0)">
                          <text 
                            textAnchor="end" 
                            dy="-4"
                            fill="#ffffff" 
                            fontSize="10" 
                            className="font-sans font-black tracking-tight"
                          >
                            {node.label}
                          </text>
                          <text 
                            textAnchor="end" 
                            dy="8"
                            fill={node.color} 
                            fontSize="8" 
                            className="font-mono font-bold tracking-wider uppercase opacity-80"
                          >
                            {node.sublabel}
                          </text>
                        </g>
                      ) : node.type === 'destination' ? (
                        <g transform="translate(25, 0)">
                          <text 
                            textAnchor="start" 
                            dy="-4"
                            fill="#ffffff" 
                            fontSize="10" 
                            className="font-sans font-black tracking-tight"
                          >
                            {node.label}
                          </text>
                          <text 
                            textAnchor="start" 
                            dy="8"
                            fill={node.color} 
                            fontSize="8" 
                            className="font-mono font-bold tracking-wider uppercase opacity-80"
                          >
                            {node.sublabel}
                          </text>
                        </g>
                      ) : (
                        // Middle processing node
                        <g transform="translate(0, -25)">
                          <text 
                            textAnchor="middle" 
                            dy="0"
                            fill="#ffffff" 
                            fontSize="10" 
                            className="font-sans font-black tracking-tight"
                          >
                            {node.label}
                          </text>
                          <text 
                            textAnchor="middle" 
                            dy="10"
                            fill={node.color} 
                            fontSize="8" 
                            className="font-mono font-bold tracking-wider uppercase opacity-85"
                          >
                            {node.sublabel}
                          </text>
                        </g>
                      )}
                    </g>
                  );
                })}
              </g>
            </g>
          </svg>
          </div>
        </div>

        {/* Right Side: Detailed node monitoring inspect deck */}
        <div className="flex flex-col gap-5">
          <div className="bg-[#02050c]/50 border border-slate-800/80 rounded-xl p-6 flex flex-col justify-between h-full min-h-[300px]">
            {selectedNode ? (
              <div className="space-y-6 text-left">
                {/* Node Identity card */}
                <div className="flex items-start gap-4">
                  <div 
                    style={{ backgroundColor: selectedNode.bgColor, borderColor: selectedNode.color }}
                    className="w-12 h-12 rounded-xl border flex items-center justify-center shrink-0"
                  >
                    <Database style={{ color: selectedNode.color }} className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-white text-sm font-sans font-extrabold tracking-tight uppercase">
                      {selectedNode.label}
                    </h4>
                    <span 
                      style={{ color: selectedNode.color }}
                      className="text-[9px] font-mono font-bold uppercase tracking-widest mt-0.5 block"
                    >
                      {selectedNode.sublabel}
                    </span>
                  </div>
                </div>

                {/* Tactical values showcase */}
                <div className="bg-[#0b1226] border border-slate-800/50 rounded-xl p-4 space-y-4">
                  <span className="text-[9px] font-mono text-slate-500 font-bold uppercase tracking-wider block">
                    OPERATIONAL TELEMETRY SUMMARY
                  </span>
                  
                  {selectedNode.id === 'GROSS_HARVEST' && (
                    <div className="space-y-1">
                      <span className="text-3xl font-mono font-black text-white block">
                        £{calculations.grossHarvest.toLocaleString()}
                      </span>
                      <span className="text-[10px] text-slate-400 font-mono block">100.0% SYSTEM REVENUE ORIGIN</span>
                    </div>
                  )}

                  {selectedNode.id === 'ZAKAT_LEVY' && (
                    <div className="space-y-1">
                      <span className="text-3xl font-mono font-black text-amber-400 block">
                        £{calculations.NexoraXLevy.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span className="text-[10px] text-slate-400 font-mono block">1.0% 3% Community Allocation HARVESTED</span>
                    </div>
                  )}

                  {selectedNode.id === 'GAVEL_FEE' && (
                    <div className="space-y-1">
                      <span className="text-3xl font-mono font-black text-violet-400 block">
                        £{calculations.lifetimeTax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span className="text-[10px] text-slate-400 font-mono block">20.0% COGNITIVE TAX CONTRIBUTION</span>
                    </div>
                  )}

                  {selectedNode.id === 'NET_SETTLEMENT' && (
                    <div className="space-y-1">
                      <span className="text-3xl font-mono font-black text-emerald-400 block">
                        £{calculations.netSettlement.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span className="text-[10px] text-slate-400 font-mono block">79.0% NET FUNDS SECURED</span>
                    </div>
                  )}

                  {selectedNode.id.startsWith('CHARITY_') && (
                    <div className="space-y-1">
                      <span className="text-3xl font-mono font-black text-amber-400 block">
                        £{calculations.charity1.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span className="text-[10px] text-slate-400 font-mono block">EQUAL 33.33% RECONCILIATION SEGMENT</span>
                    </div>
                  )}

                  {selectedNode.id === 'HMRC_REVENUE' && (
                    <div className="space-y-1">
                      <span className="text-3xl font-mono font-black text-white block">
                        £{calculations.lifetimeTax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span className="text-[10px] text-slate-400 font-mono block">STATUTORY DEBT LIQUIDATED</span>
                    </div>
                  )}

                  {selectedNode.id === 'SecurePayment_BANK_SETTLEMENT' && (
                    <div className="space-y-1">
                      <span className="text-3xl font-mono font-black text-emerald-400 block">
                        £{calculations.netSettlement.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span className="text-[10px] text-slate-400 font-mono block">SecurePayment ROUTER ONLINE: SORT Code Verified Payment Configuration</span>
                    </div>
                  )}
                </div>

                {/* Gateway Integration Trigger Quick Button */}
                <button
                  type="button"
                  onClick={() => triggerQrSync(selectedNode.id)}
                  className="w-full py-2.5 bg-cyan-500/10 hover:bg-cyan-500 hover:text-black border border-cyan-500/40 text-cyan-400 font-bold uppercase tracking-wider text-[10px] rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1.5 shadow-[0_0_15px_rgba(6,182,212,0.15)] font-mono"
                >
                  <QrCode className="w-4 h-4" />
                  Generate Dynamic Gateway QR Code (Empire OS Core)
                </button>

                {/* Dynamic diagnostic audit trail checklist */}
                <div className="space-y-3">
                  <h5 className="text-[10px] font-mono text-slate-400 uppercase tracking-widest font-bold">
                    SECURED ATTRIBUTES & REPLICATION
                  </h5>
                  <div className="space-y-2 font-mono text-[9px] uppercase tracking-wider text-slate-300">
                    {selectedNode.details.map((detail, idx) => (
                      <div key={idx} className="flex items-start gap-2 border-b border-slate-800/20 pb-2">
                        <CheckCircle className="w-3.5 h-3.5 text-cyan-500 shrink-0 mt-0.5" />
                        <span>{detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center text-center py-10 text-slate-500 space-y-3">
                <Info className="w-10 h-10 text-slate-600 animate-pulse" />
                <span className="text-[10px] font-mono uppercase tracking-widest">
                  SELECT ANY NODE WITHIN THE FLOW MATRIX TO RECONCILE PROTOCOLS
                </span>
              </div>
            )}

            <div className="mt-8 border-t border-slate-800/60 pt-4 text-left font-mono text-[9px] text-slate-500 uppercase leading-relaxed space-y-2">
              <div className="flex justify-between">
                <span>ACTIVE MONITORING CHANNEL:</span>
                <span className="text-cyan-400 font-bold">CloudSync-02 SYNC</span>
              </div>
              <div className="flex justify-between">
                <span>SecurePayment REGULATED ESCROW:</span>
                <span className="text-emerald-400 font-bold">ACTIVE</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dynamic Empire OS Core Registry QR Modal Overlay */}
      {activeQrData && activeQrData.isOpen && (
        <div className="absolute inset-0 z-50 bg-[#02050c]/85 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-[#050914] border border-amber-500/50 p-6 rounded-2xl max-w-sm w-full shadow-[0_0_50px_rgba(245,158,11,0.25)] space-y-4 text-center text-white relative animate-fadeIn">
            {/* Header info */}
            <div>
              <span className="text-[8px] font-sans text-amber-500 font-extrabold uppercase tracking-[0.25em] block mb-1">
                EMPIRE OS CORE REGISTRY
              </span>
              <h3 className="text-sm font-mono text-cyan-400 font-black tracking-[0.15em] uppercase">
                DYNAMIC QR CODE ACTIVATED
              </h3>
            </div>

            {/* Loading / QR display space */}
            {activeQrData.isLoading ? (
              <div className="h-44 flex flex-col items-center justify-center space-y-3">
                <RefreshCw className="w-8 h-8 text-amber-500 animate-spin" />
                <span className="text-[9px] font-mono text-slate-400 uppercase tracking-widest animate-pulse">
                  Querying Core Registry...
                </span>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-white p-3 rounded-xl inline-block relative border border-slate-200/20 shadow-inner mx-auto">
                  {/* Real physical fallback image element */}
                  <img 
                    src={`/${activeQrData.qrAsset}`} 
                    alt={`${activeQrData.label} QR`}
                    className="w-40 h-40 object-contain mx-auto bg-white"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  
                  {/* Deterministic dynamically rendered vector matrix core */}
                  <svg className="w-40 h-40 mx-auto bg-white font-mono" viewBox="0 0 256 256">
                    <rect width="100%" height="100%" fill="#ffffff" />
                    {(() => {
                      const blocks = generateQRBlocks(activeQrData.link || "https://pay.SecurePayment.co/pay/9fd1aa7e-20b1-44b0-bf57-b75944dd5134");
                      const size = blocks.length;
                      const boxSize = 256;
                      const padding = 20;
                      const cellSize = (boxSize - padding * 2) / size;

                      return blocks.map((row, r) => {
                        return row.map((isActive, c) => {
                          if (!isActive) return null;
                          const isFinderPattern = (r < 7 && (c < 7 || c >= size - 7)) || (r >= size - 7 && c < 7);
                          const fillColor = isFinderPattern ? '#06b6d4' : '#0f172a';
                          return (
                            <rect
                              key={`${r}-${c}`}
                              x={padding + c * cellSize}
                              y={padding + r * cellSize}
                              width={cellSize - 0.5}
                              height={cellSize - 0.5}
                              rx={1.2}
                              fill={fillColor}
                            />
                          );
                        });
                      });
                    })()}
                    <circle cx={128} cy={128} r={14} fill="#0f172a" />
                    <circle cx={128} cy={128} r={9} fill="#f59e0b" stroke="#ffffff" strokeWidth={1.5} />
                    <text x={128} y={128 + 2.5} fontSize={7} fontWeight="black" fill="#ffffff" textAnchor="middle">NX</text>
                  </svg>
                </div>

                {/* Info and note */}
                <div className="bg-[#0b1226]/80 p-3 border border-slate-800 rounded-lg text-left space-y-1">
                  <p className="text-[8px] font-mono text-amber-500 font-extrabold uppercase tracking-widest text-center">
                    GATEWAY: {activeQrData.label}
                  </p>
                  <p className="text-[9px] font-mono text-zinc-300 leading-normal text-center">
                    {activeQrData.note}
                  </p>
                </div>

                <div className="flex gap-2">
                  <a
                    href={activeQrData.link}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 flex items-center justify-center gap-1.5 bg-amber-400 hover:bg-amber-300 text-black font-extrabold text-[10px] py-2 uppercase tracking-wider transition-all rounded shadow-[0_0_10px_rgba(245,158,11,0.3)] cursor-pointer"
                  >
                    Direct Portal →
                  </a>
                  <button
                    type="button"
                    onClick={() => setActiveQrData(null)}
                    className="flex-1 bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 font-bold text-[10px] py-2 uppercase tracking-wider transition-all rounded cursor-pointer"
                  >
                    Close Sync
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
